import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Pencil, Save, Sparkles, X, ChevronDown } from 'lucide-react';
import { updateInspirationalDesign } from '../api/inspirationalDesign.api';
import { analyzeGarment } from '../api/garmentAnalysis.api';
import { PRODUCT_CATEGORIES } from '../data/productCategories';
import {
  GARMENT_FABRIC_ATTR_SELECT_OPTIONS,
  mergeGarmentFabricSelectOptions,
} from '../data/garmentFabricAttributeOptions';

const FABRIC_ATTRIBUTE_LABELS = {
  fabricTypes: 'Fabric types',
  weaveOrKnit: 'Weave / knit',
  weight: 'Weight',
  stretch: 'Stretch',
  opacity: 'Opacity',
  drape: 'Drape',
  texture: 'Texture',
  visualType: 'Fabric visual',
  seasonSuitability: 'Season suitability',
  constructionNotes: 'Construction notes',
};

const FABRIC_ATTRIBUTE_KEYS = Object.keys(FABRIC_ATTRIBUTE_LABELS);

const IMAGE_KIND_OPTIONS = [
  { value: 'sketch', label: 'Sketch / flat' },
  { value: 'real_design', label: 'Real / photo' },
];

const CONFIDENCE_OPTIONS = ['high', 'medium', 'low'];

function normalizeList(value) {
  if (Array.isArray(value)) return value.map((item) => String(item || '').trim()).filter(Boolean);
  if (value) return [String(value).trim()].filter(Boolean);
  return [];
}

function listToInput(values) {
  return normalizeList(values).join(', ');
}

function inputToList(text) {
  return String(text || '')
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function formatProductCategoryLabel(value) {
  const map = {
    'women-clothing': 'Women',
    'men-clothing': 'Men',
    'kids-boy': 'Kids (boy)',
    'kids-girl': 'Kids (girl)',
  };
  return map[value] || value || '—';
}

function formatProductTypeDisplay(ga) {
  if (!ga) return '—';
  if (ga.productType === 'other' && ga.otherTypeDescription) {
    return `Other (${ga.otherTypeDescription})`;
  }
  if (ga.productType === 'other') return 'Other';
  return ga.productType ? ga.productType.replace(/-/g, ' ') : '—';
}

function formatImageKind(ik) {
  if (ik === 'real_design') return 'Real / photo';
  if (ik === 'sketch') return 'Sketch / flat';
  return ik || '—';
}

function getProductTypesForCategory(categoryValue) {
  const cat = PRODUCT_CATEGORIES.find((c) => c.value === categoryValue);
  if (!cat) return [{ label: 'Other', value: 'other' }];
  const types = [];
  for (const t of cat.types || []) {
    types.push({ label: t.label, value: t.value });
    for (const st of t.subTypes || []) {
      types.push({ label: `${t.label} · ${st.label}`, value: st.value });
    }
  }
  if (!types.some((t) => t.value === 'other')) {
    types.push({ label: 'Other', value: 'other' });
  }
  return types;
}

function emptySuitableFabricAttributes() {
  return FABRIC_ATTRIBUTE_KEYS.reduce((acc, key) => {
    acc[key] = [];
    return acc;
  }, {});
}

function cloneAnalysisForEdit(analysis) {
  const base = analysis && typeof analysis === 'object' ? analysis : {};
  const sfa = base.suitableFabricAttributes || {};
  return {
    productCategory: base.productCategory || '',
    productType: base.productType || '',
    otherTypeDescription: base.otherTypeDescription || '',
    imageKind: base.imageKind || '',
    confidence: base.confidence || '',
    rationale: base.rationale || '',
    tags: normalizeList(base.tags),
    suitableFabricAttributes: {
      ...emptySuitableFabricAttributes(),
      ...Object.fromEntries(
        FABRIC_ATTRIBUTE_KEYS.map((key) => [key, normalizeList(sfa[key])])
      ),
    },
    attributes: Array.isArray(base.attributes)
      ? base.attributes.map((attr) => ({
          key: attr.key || '',
          label: attr.label || attr.key || '',
          values: normalizeList(attr.values),
          confidence: attr.confidence || '',
          evidence: attr.evidence || '',
        }))
      : [],
    customizationOptions: Array.isArray(base.customizationOptions)
      ? base.customizationOptions.map((opt) => ({
          key: opt.key || '',
          label: opt.label || opt.key || '',
          selectedValues: normalizeList(opt.selectedValues),
          availableValues: normalizeList(opt.availableValues),
          confidence: opt.confidence || '',
          evidence: opt.evidence || '',
        }))
      : [],
    analyzedAt: base.analyzedAt,
    modelUsed: base.modelUsed,
  };
}

function buildSavePayload(draft) {
  return {
    productCategory: String(draft.productCategory || '').trim(),
    productType: String(draft.productType || '').trim(),
    otherTypeDescription: String(draft.otherTypeDescription || '').trim(),
    imageKind: String(draft.imageKind || '').trim(),
    confidence: String(draft.confidence || '').trim(),
    rationale: String(draft.rationale || '').trim(),
    tags: normalizeList(draft.tags),
    suitableFabricAttributes: {
      ...emptySuitableFabricAttributes(),
      ...Object.fromEntries(
        FABRIC_ATTRIBUTE_KEYS.map((key) => [
          key,
          normalizeList(draft.suitableFabricAttributes?.[key]),
        ])
      ),
    },
    attributes: (draft.attributes || [])
      .map((attr) => ({
        key: String(attr.key || '').trim(),
        label: String(attr.label || attr.key || '').trim(),
        values: normalizeList(attr.values),
        confidence: String(attr.confidence || '').trim(),
        evidence: String(attr.evidence || '').trim(),
      }))
      .filter((attr) => attr.key && attr.values.length > 0),
    customizationOptions: (draft.customizationOptions || [])
      .map((opt) => ({
        key: String(opt.key || '').trim(),
        label: String(opt.label || opt.key || '').trim(),
        selectedValues: normalizeList(opt.selectedValues),
        availableValues: normalizeList(opt.availableValues),
        confidence: String(opt.confidence || '').trim(),
        evidence: String(opt.evidence || '').trim(),
      }))
      .filter((opt) => opt.key && opt.selectedValues.length > 0),
    customization: (draft.attributes || []).reduce((acc, attr) => {
      const values = normalizeList(attr.values);
      if (attr.key && values.length) acc[attr.key] = values;
      return acc;
    }, {}),
    analyzedAt: draft.analyzedAt || new Date().toISOString(),
    modelUsed: draft.modelUsed || '',
  };
}

function GarmentFabricAttributeField({ fieldKey, label, values, onChange }) {
  const selected = normalizeList(values);
  const baseOptions = GARMENT_FABRIC_ATTR_SELECT_OPTIONS[fieldKey] || [];
  const allOptions = mergeGarmentFabricSelectOptions(selected, baseOptions);
  const remaining = allOptions.filter((opt) => !selected.includes(opt));

  const setSelected = (next) => onChange(normalizeList(next));

  const addValue = (value) => {
    if (!value || selected.includes(value)) return;
    setSelected([...selected, value]);
  };

  const removeValue = (value) => {
    setSelected(selected.filter((item) => item !== value));
  };

  const selectClassName =
    'mt-1 w-full appearance-none rounded-lg border border-slate-200 bg-white px-2.5 py-2 pr-8 text-sm text-slate-800 shadow-sm focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-100';

  return (
    <label className="block">
      <span className="text-[11px] font-semibold text-slate-600">{label}</span>
      {selected.length > 0 ? (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {selected.map((value) => (
            <span
              key={value}
              className="inline-flex max-w-full items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-900"
            >
              <span className="truncate">{value}</span>
              <button
                type="button"
                onClick={() => removeValue(value)}
                className="shrink-0 rounded-full p-0.5 text-emerald-700 hover:bg-emerald-200/80"
                aria-label={`Remove ${value}`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <div className="relative mt-1.5">
        <select
          value=""
          onChange={(e) => {
            addValue(e.target.value);
            e.target.value = '';
          }}
          className={selectClassName}
          disabled={remaining.length === 0}
        >
          <option value="">
            {remaining.length === 0 ? 'All options selected' : `Add ${label.toLowerCase()}…`}
          </option>
          {remaining.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        <ChevronDown size={14} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
      </div>
      {selected.length > 1 ? (
        <button
          type="button"
          onClick={() => setSelected([])}
          className="mt-1 text-[10px] font-semibold text-slate-500 hover:text-slate-700"
        >
          Clear all
        </button>
      ) : null}
    </label>
  );
}

function ConstructionNotesField({ values, onChange }) {
  const text = normalizeList(values).join('\n');
  const className =
    'mt-1 w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm text-slate-800 shadow-sm focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-100';

  return (
    <label className="block sm:col-span-2">
      <span className="text-[11px] font-semibold text-slate-600">Construction notes</span>
      <textarea
        rows={2}
        value={text}
        onChange={(e) => {
          const next = String(e.target.value || '')
            .split(/\n/)
            .map((line) => line.trim())
            .filter(Boolean);
          onChange(next);
        }}
        placeholder="Notes for fabric choice and construction"
        className={className}
      />
    </label>
  );
}

function CommaListField({ label, value, onChange, placeholder, rows = 1 }) {
  const isMultiline = rows > 1;
  const className =
    'mt-1 w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm text-slate-800 shadow-sm focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-100';
  return (
    <label className="block">
      <span className="text-[11px] font-semibold text-slate-600">{label}</span>
      {isMultiline ? (
        <textarea
          rows={rows}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={className}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={className}
        />
      )}
      <span className="mt-0.5 block text-[10px] text-slate-400">Separate multiple values with commas</span>
    </label>
  );
}

function GarmentAnalysisEditorPanel({
  analysis,
  designId,
  designImageUrl,
  onSaved,
  showNotification,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);

  const productTypeOptions = useMemo(
    () => getProductTypesForCategory(draft?.productCategory || analysis?.productCategory),
    [draft?.productCategory, analysis?.productCategory]
  );

  const startEditing = useCallback(() => {
    setDraft(cloneAnalysisForEdit(analysis));
    setEditing(true);
  }, [analysis]);

  const cancelEditing = useCallback(() => {
    setEditing(false);
    setDraft(null);
  }, []);

  useEffect(() => {
    if (!editing) return;
    setDraft(cloneAnalysisForEdit(analysis));
  }, [analysis, editing]);

  const updateDraft = (patch) => {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const updateFabricAttrList = (key, values) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        suitableFabricAttributes: {
          ...prev.suitableFabricAttributes,
          [key]: normalizeList(values),
        },
      };
    });
  };

  const updateAttribute = (index, patch) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = [...(prev.attributes || [])];
      next[index] = { ...next[index], ...patch };
      return { ...prev, attributes: next };
    });
  };

  const addAttribute = () => {
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        attributes: [
          ...(prev.attributes || []),
          { key: '', label: '', values: [], confidence: '', evidence: '' },
        ],
      };
    });
  };

  const removeAttribute = (index) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        attributes: (prev.attributes || []).filter((_, i) => i !== index),
      };
    });
  };

  const handleSave = async () => {
    if (!designId || !draft) return;
    setSaving(true);
    try {
      const garmentAnalysis = buildSavePayload(draft);
      const updated = await updateInspirationalDesign(designId, { garmentAnalysis });
      onSaved?.(updated);
      setEditing(false);
      setDraft(null);
      showNotification?.('Garment analysis saved', 'success');
    } catch (e) {
      console.error(e);
      showNotification?.(e?.response?.data?.message || e?.message || 'Could not save analysis', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReanalyze = async () => {
    const url = String(designImageUrl || '').trim();
    if (!url) {
      showNotification?.('Garment image is required to run analysis', 'error');
      return;
    }
    setReanalyzing(true);
    try {
      const ar = await analyzeGarment(url);
      if (!ar?.success || !ar.data?.analysis) {
        throw new Error(ar?.message || 'Analysis failed');
      }
      const merged = {
        ...ar.data.analysis,
        analyzedAt: new Date().toISOString(),
        modelUsed: ar.data.modelUsed,
      };
      const updated = await updateInspirationalDesign(designId, { garmentAnalysis: merged });
      onSaved?.(updated);
      if (editing) setDraft(cloneAnalysisForEdit(merged));
      showNotification?.('Garment analysis updated from image', 'success');
    } catch (e) {
      console.error(e);
      showNotification?.(e?.response?.data?.message || e?.message || 'Garment analysis failed', 'error');
    } finally {
      setReanalyzing(false);
    }
  };

  const hasAnalysis =
    analysis &&
    (analysis.productCategory ||
      analysis.rationale ||
      FABRIC_ATTRIBUTE_KEYS.some((k) => normalizeList(analysis.suitableFabricAttributes?.[k]).length > 0));

  const viewTags = normalizeList(analysis?.tags);
  const viewFabricRows = FABRIC_ATTRIBUTE_KEYS.map((key) => ({
    key,
    label: FABRIC_ATTRIBUTE_LABELS[key],
    values: normalizeList(analysis?.suitableFabricAttributes?.[key]),
  }));

  return (
    <div className="rounded-2xl border border-purple-100/90 bg-white p-4 shadow-sm sm:rounded-xl sm:p-6 max-md:shadow-md">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="h-7 w-1 shrink-0 rounded-full bg-gradient-to-b from-purple-500 to-fuchsia-500" aria-hidden />
          <h2 className="text-base font-bold tracking-tight text-slate-900 sm:text-lg">Garment analysis</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {designImageUrl ? (
            <button
              type="button"
              onClick={handleReanalyze}
              disabled={reanalyzing || saving}
              className="inline-flex items-center gap-1.5 rounded-lg border border-purple-200 bg-purple-50 px-3 py-1.5 text-xs font-semibold text-purple-900 hover:bg-purple-100 disabled:opacity-50"
            >
              {reanalyzing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              Re-run AI
            </button>
          ) : null}
          {editing ? (
            <>
              <button
                type="button"
                onClick={cancelEditing}
                disabled={saving}
                className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                <X className="h-3.5 w-3.5" />
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-1 rounded-lg bg-purple-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-purple-700 disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                Save
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={startEditing}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 hover:bg-slate-50"
            >
              <Pencil className="h-3.5 w-3.5" />
              {hasAnalysis ? 'Edit' : 'Add analysis'}
            </button>
          )}
        </div>
      </div>

      {editing && draft ? (
        <div className="mt-5 space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-[11px] font-semibold text-slate-600">Audience / category</span>
              <select
                value={draft.productCategory}
                onChange={(e) =>
                  updateDraft({
                    productCategory: e.target.value,
                    productType:
                      getProductTypesForCategory(e.target.value)[0]?.value || draft.productType,
                  })
                }
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm"
              >
                <option value="">Select category</option>
                {PRODUCT_CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold text-slate-600">Product type</span>
              <select
                value={draft.productType}
                onChange={(e) => updateDraft({ productType: e.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm"
              >
                <option value="">Select type</option>
                {productTypeOptions.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            {draft.productType === 'other' ? (
              <label className="block sm:col-span-2">
                <span className="text-[11px] font-semibold text-slate-600">Other type description</span>
                <input
                  type="text"
                  value={draft.otherTypeDescription}
                  onChange={(e) => updateDraft({ otherTypeDescription: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm"
                />
              </label>
            ) : null}
            <label className="block">
              <span className="text-[11px] font-semibold text-slate-600">Image kind</span>
              <select
                value={draft.imageKind}
                onChange={(e) => updateDraft({ imageKind: e.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm"
              >
                <option value="">—</option>
                {IMAGE_KIND_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold text-slate-600">Confidence</span>
              <select
                value={draft.confidence}
                onChange={(e) => updateDraft({ confidence: e.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm"
              >
                <option value="">—</option>
                {CONFIDENCE_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="text-[11px] font-semibold text-slate-600">Rationale</span>
            <textarea
              rows={3}
              value={draft.rationale}
              onChange={(e) => updateDraft({ rationale: e.target.value })}
              className="mt-1 w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm"
            />
          </label>

          <CommaListField
            label="Tags"
            value={listToInput(draft.tags)}
            onChange={(text) => updateDraft({ tags: inputToList(text) })}
            placeholder="e.g. women top, full sleeves, casual"
          />

          <div className="rounded-xl border border-emerald-200/70 bg-emerald-50/40 p-4">
            <p className="text-xs font-bold uppercase tracking-wide text-emerald-900">
              Recommended fabric properties
            </p>
            <p className="mt-1 text-[11px] text-emerald-800/80">
              Used to filter and rank recommended fabrics for this garment.
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {FABRIC_ATTRIBUTE_KEYS.map((key) =>
                key === 'constructionNotes' ? (
                  <ConstructionNotesField
                    key={key}
                    values={draft.suitableFabricAttributes?.[key]}
                    onChange={(values) => updateFabricAttrList(key, values)}
                  />
                ) : (
                  <GarmentFabricAttributeField
                    key={key}
                    fieldKey={key}
                    label={FABRIC_ATTRIBUTE_LABELS[key]}
                    values={draft.suitableFabricAttributes?.[key]}
                    onChange={(values) => updateFabricAttrList(key, values)}
                  />
                )
              )}
            </div>
          </div>

          <div className="rounded-xl border border-violet-200/70 bg-violet-50/30 p-4">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-bold uppercase tracking-wide text-violet-900">
                Garment customization attributes
              </p>
              <button
                type="button"
                onClick={addAttribute}
                className="text-xs font-semibold text-violet-700 hover:text-violet-900"
              >
                + Add attribute
              </button>
            </div>
            {(draft.attributes || []).length === 0 ? (
              <p className="mt-2 text-xs text-slate-500">No customization attributes. Add rows if needed.</p>
            ) : (
              <div className="mt-3 space-y-3">
                {(draft.attributes || []).map((attr, index) => (
                  <div
                    key={`attr-${index}`}
                    className="rounded-lg border border-violet-100 bg-white p-3 space-y-2"
                  >
                    <div className="flex justify-between gap-2">
                      <span className="text-[10px] font-bold uppercase text-slate-500">Attribute {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeAttribute(index)}
                        className="text-[10px] font-semibold text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <input
                        type="text"
                        value={attr.key}
                        onChange={(e) => updateAttribute(index, { key: e.target.value })}
                        placeholder="Key (e.g. sleeve)"
                        className="rounded border border-slate-200 px-2 py-1.5 text-sm"
                      />
                      <input
                        type="text"
                        value={attr.label}
                        onChange={(e) => updateAttribute(index, { label: e.target.value })}
                        placeholder="Label (e.g. Sleeves)"
                        className="rounded border border-slate-200 px-2 py-1.5 text-sm"
                      />
                    </div>
                    <CommaListField
                      label="Values"
                      value={listToInput(attr.values)}
                      onChange={(text) => updateAttribute(index, { values: inputToList(text) })}
                      placeholder="Selected values"
                    />
                    <div className="grid gap-2 sm:grid-cols-2">
                      <label className="block">
                        <span className="text-[10px] font-semibold text-slate-500">Confidence</span>
                        <select
                          value={attr.confidence}
                          onChange={(e) => updateAttribute(index, { confidence: e.target.value })}
                          className="mt-0.5 w-full rounded border border-slate-200 px-2 py-1.5 text-sm"
                        >
                          <option value="">—</option>
                          {CONFIDENCE_OPTIONS.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <label className="block">
                      <span className="text-[10px] font-semibold text-slate-500">Evidence</span>
                      <textarea
                        rows={2}
                        value={attr.evidence}
                        onChange={(e) => updateAttribute(index, { evidence: e.target.value })}
                        className="mt-0.5 w-full rounded border border-slate-200 px-2 py-1.5 text-sm"
                      />
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : !hasAnalysis ? (
        <div className="mt-4 rounded-xl border border-dashed border-purple-200/90 bg-purple-50/60 p-6 text-center text-sm text-slate-600">
          <Sparkles className="mx-auto mb-2 h-8 w-8 text-purple-300" />
          <p>No garment analysis yet.</p>
          <p className="mt-1 text-xs text-slate-500">
            Run AI analysis or click Add analysis to enter properties manually.
          </p>
        </div>
      ) : (
        <>
          <dl className="mt-4 flex w-full min-w-0 flex-col gap-0 overflow-hidden rounded-xl border border-slate-200/70 bg-slate-50/30 sm:grid sm:grid-cols-2 sm:gap-3 sm:border-0 sm:bg-transparent">
            {[
              { label: 'Audience', value: formatProductCategoryLabel(analysis.productCategory) },
              { label: 'Product type', value: formatProductTypeDisplay(analysis) },
              { label: 'Image kind', value: formatImageKind(analysis.imageKind) },
              { label: 'Confidence', value: analysis.confidence ? String(analysis.confidence) : '—' },
            ].map(({ label, value }) => (
              <div
                key={label}
                className="border-b border-slate-200/60 bg-white/80 px-3.5 py-3 last:border-b-0 sm:rounded-lg sm:border-0 sm:bg-slate-50 sm:px-3 sm:py-2"
              >
                <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 sm:text-[11px]">
                  {label}
                </dt>
                <dd className="mt-1 text-[13px] font-semibold leading-snug text-slate-800 sm:mt-0.5 sm:text-sm sm:font-medium">
                  {value}
                </dd>
              </div>
            ))}
          </dl>
          {analysis.rationale ? (
            <div className="mt-4 rounded-xl border border-slate-100 bg-gradient-to-br from-slate-50/90 to-white p-3.5">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-purple-600/90">Rationale</p>
              <p className="mt-2 whitespace-pre-wrap text-[13px] leading-relaxed text-slate-700 sm:text-sm">
                {analysis.rationale}
              </p>
            </div>
          ) : null}
          {viewTags.length > 0 ? (
            <div className="mt-4 rounded-xl border border-violet-100/80 bg-violet-50/50 p-3.5">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-violet-700">Tags</p>
              <div className="mt-2.5 flex flex-wrap gap-2">
                {viewTags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex max-w-full break-words rounded-full bg-white px-2.5 py-1 text-xs font-medium text-violet-800 ring-1 ring-violet-100/90"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          {(analysis.attributes || []).length > 0 ? (
            <div className="mt-4 rounded-xl border border-violet-100/80 bg-violet-50/40 p-3.5">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-violet-800">
                Customization attributes
              </p>
              <ul className="mt-2 space-y-2">
                {(analysis.attributes || []).map((attr) => (
                  <li key={attr.key} className="text-sm text-slate-700">
                    <span className="font-semibold">{attr.label || attr.key}:</span>{' '}
                    {normalizeList(attr.values).join(', ') || '—'}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="mt-4 w-full min-w-0 overflow-hidden rounded-xl border border-emerald-200/60 bg-gradient-to-br from-emerald-50/70 to-teal-50/25 p-3 sm:p-3.5">
            <p className="text-[10px] font-bold uppercase tracking-wide text-emerald-900">
              Recommended fabric properties
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {viewFabricRows.map(({ key, label, values }) => (
                <div
                  key={key}
                  className={`rounded-lg bg-white/90 p-3 shadow-sm ring-1 ring-emerald-100/80 ${values.length === 0 ? 'opacity-60' : ''}`}
                >
                  <p className="text-xs font-semibold text-slate-700">{label}</p>
                  {values.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {values.map((value) => (
                        <span
                          key={value}
                          className="inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-900"
                        >
                          {value}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-1 text-[11px] text-slate-400">—</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default GarmentAnalysisEditorPanel;
export { FABRIC_ATTRIBUTE_LABELS, FABRIC_ATTRIBUTE_KEYS, normalizeList };
